import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom-v5-compat'

import { UploadSource } from '@buffer-mono/uploader'

import useIdeasUploader from '~publish/hooks/useIdeasUploader'
import { useOnce } from '~publish/hooks/useOnce'
import { useQueryParam } from '~publish/hooks/useQueryParam'
import { selectAllTags } from '~publish/legacy/campaign/slice'
import { useAppSelector } from '~publish/legacy/store'
import type { Tag } from '~publish/pages/Create/types'

import { IdeaComposer, type NewIdea } from '../IdeaComposer'
import { useNewIdeaComposerSplit } from '../IdeaComposer/hooks'
import { IdeaComposer as LegacyIdeaComposer } from '../LegacyIdeaComposer/IdeaComposer'

/**
 * Component used to open IdeaComposer with preselected data.
 * Renders when corresponding route is matched.
 */
export function IdeaCreationDialog(): JSX.Element | null {
  const [open, setOpen] = React.useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  const { isEnabled: isNewIdeaComposerEnabled } = useNewIdeaComposerSplit()

  const [text = undefined] = useQueryParam('text')
  const [title = undefined] = useQueryParam('title')
  const [groupId] = useQueryParam('groupId')
  const [templateId] = useQueryParam('templateId')

  // We select tags for prefilling from both the underlying page's tag filters
  // and any tags passed in as query params
  // We handle these separately because sharing the same parameter name
  // causes the underlying page's tag filters to be overwritten
  const allTags: Tag[] = useAppSelector(selectAllTags)
  // Tags selected in the underlying page's tag filters
  const [tagFilterTagIds = []] = useQueryParam<string[]>('tagIds')
  const [prefillTagIds = []] = useQueryParam<string[]>('tags')

  // Tags provided as query params specifically to be preselected
  const mergedTagIds = [...new Set([...tagFilterTagIds, ...prefillTagIds])]

  // Map tags down to required properties to omit unnecessary data
  const prefillTags = allTags
    .filter((tag) => mergedTagIds.includes(tag.id))
    .map((tag) => ({
      id: tag.id,
      name: tag.name,
      color: tag.color,
    }))

  const initialIdea: NewIdea = {
    groupId,
    templateId,
    content: {
      title,
      text,
      tags: prefillTags,
      services: [],
    },
  }

  // Preload any media passed in query params
  const [media = []] = useQueryParam<string[]>('media')
  const uploader = useIdeasUploader()

  // Only need to run once - media is only provided in
  // initial query param
  useOnce((): boolean => {
    if (media) {
      void uploader.addFilesFromUrls(
        media.map((url) => ({
          remoteUrl: url,
          useResizer: true,
        })),
        UploadSource.extension(),
      )
    }
    return !!media
  }, [media, uploader])

  const handleOpenChange = (open: boolean): void => {
    setOpen(open)
    if (!open) {
      /**
       * When this modal is closed, we want to navigate back to the original page
       * that opened the composer to ensure a seamless experience.
       * The '..' path will navigate up one level in the route tree.
       * (This is based on the component routing levels, not literal path segments)
       *
       * Keeping the `search` (query params) is a bit of a hack, but it prevents
       * re-rendering the /create page, since without params the page will auto-add
       * the ?view=board and thus cause a flash/re-render + resetting scroll,
       * which is not what we want.
       */
      navigate({
        pathname: '..',
        search: location.search,
      })
    }
  }

  return isNewIdeaComposerEnabled ? (
    <IdeaComposer
      initialIdea={initialIdea}
      open={open}
      onOpenChange={handleOpenChange}
      uploader={uploader}
    />
  ) : (
    <LegacyIdeaComposer
      initialIdea={initialIdea}
      open={open}
      onOpenChange={handleOpenChange}
      uploader={uploader}
    />
  )
}
